import { Directive } from '../../../types';

/**
 * ID range for Kubernetes user/group configuration
 */
export interface IdRange {
  min: number;
  max: number;
}

/**
 * Host path configuration for Kubernetes
 */
export interface AllowedHostPath {
  pathPrefix: string;
  readonly: boolean;
}

/**
 * SELinux option configuration
 */
export interface SeLinuxOption {
  level: string;
  role: string;
  type: string;
  user: string;
}

/**
 * Form data for Kubernetes Security Policy
 * These are the values displayed and edited in the form (UI representation)
 */
export interface KubernetesSecurityFormData {
  // Security section
  /** Restrict access to the default namespace */
  restrictDefaultNamespace: boolean;
  /** Restrict secret contents access for non-admins (UI only) */
  restrictSecrets: boolean;

  // Pod security constraints
  /** Enable pod security constraints */
  enablePodSecurityConstraints: boolean;
  /** Restrict running privileged containers */
  restrictPrivilegedContainers: boolean;
  /** Restrict host namespace (PID, IPC) */
  restrictHostNamespace: boolean;
  /** Restrict host networking ports */
  restrictHostNetworkingPorts: boolean;
  /** Allow host network */
  hostNetwork: boolean;
  /** Minimum host port */
  hostPortMin: number;
  /** Maximum host port */
  hostPortMax: number;
  /** Restrict volume types */
  restrictVolumeTypes: boolean;
  /** Allowed volume types (e.g. configMap, secret, persistentVolumeClaim) */
  allowedVolumeTypes: string[];
  /** Restrict host filesystem paths */
  restrictHostFilesystemPaths: boolean;
  /** Allowed host paths */
  allowedHostPaths: AllowedHostPath[];
  /** Restrict FlexVolume drivers */
  restrictDrivers: boolean;
  /** Allowed FlexVolume drivers */
  allowedFlexVolumeDrivers: string[];
  /** Require read-only root filesystem */
  requireReadOnlyRootFilesystem: boolean;
  /** Restrict user and group IDs */
  restrictUserAndGroupIds: boolean;
  /** Run as user type (MustRunAs, MustRunAsNonRoot, RunAsAny) */
  runAsUserType: string;
  /** Run as user ID ranges */
  runAsUserRanges: IdRange[];
  /** Run as group type (MustRunAs, MustRunAsNonRoot, RunAsAny) */
  runAsGroupType: string;
  /** Run as group ID ranges */
  runAsGroupRanges: IdRange[];
  /** Supplemental groups type (MustRunAs, MustRunAsNonRoot, RunAsAny) */
  supplementalGroupsType: string;
  /** Supplemental groups ID ranges */
  supplementalGroupsRanges: IdRange[];
  /** FS group type (MustRunAs, MustRunAsNonRoot, RunAsAny) */
  fsGroupType: string;
  /** FS group ID ranges */
  fsGroupRanges: IdRange[];
  /** Restrict escalation to root privileges */
  restrictEscalationToRootPrivileges: boolean;
  /** Restrict Linux capabilities */
  restrictLinuxCapabilities: boolean;
  /** Allowed capabilities */
  allowedCapabilities: string[];
  /** Required drop capabilities */
  requiredDropCapabilities: string[];
  /** Default add capabilities */
  defaultAddCapabilities: string[];
  /** Restrict SELinux security context */
  restrictSeLinuxSecurityContext: boolean;
  /** SELinux options */
  seLinuxOptions: SeLinuxOption[];
  /** Restrict proc mount types */
  restrictProcMountTypes: boolean;
  /** Restrict AppArmor profiles */
  restrictAppArmorProfiles: boolean;
  /** Allowed AppArmor profiles */
  allowedAppArmorProfiles: string[];
  /** Restrict seccomp profiles */
  restrictSeccompProfiles: boolean;
  /** Allowed seccomp profiles */
  allowedSeccompProfiles: string[];
  /** Restrict sysctl profiles */
  restrictSysctlProfiles: boolean;
  /** Forbidden sysctls */
  forbiddenSysctls: string[];
}

/**
 * Stored/API payload for Kubernetes Security Policy
 * This matches the actual backend API structure with nested objects
 */
export interface KubernetesSecurityPolicyPayload {
  restrictDefaultNamespace: Directive<boolean>;
  restrictSecrets: Directive<boolean>;
  enablePodSecurityConstraints: Directive<boolean>;
  privilegedContainers: Directive<{ enabled: boolean }>;
  hostNamespaces: Directive<{ enabled: boolean }>;
  hostPorts: Directive<{
    enabled: boolean;
    hostNetwork: boolean;
    min: number;
    max: number;
  }>;
  volumeTypes: Directive<{
    enabled: boolean;
    allowedTypes: string[];
  }>;
  hostFilesystem: Directive<{
    enabled: boolean;
    allowedPaths: AllowedHostPath[];
  }>;
  allowFlexVolumes: Directive<{
    enabled: boolean;
    allowedVolumes: string[];
  }>;
  readOnlyRootFileSystem: Directive<{ enabled: boolean }>;
  users: Directive<{
    enabled: boolean;
    runAsUser: {
      type: string;
      idrange: IdRange[];
    };
    runAsGroup: {
      type: string;
      idrange: IdRange[];
    };
    supplementalGroups: {
      type: string;
      idrange: IdRange[];
    };
    fsGroups: {
      type: string;
      idrange: IdRange[];
    };
  }>;
  allowPrivilegeEscalation: Directive<{ enabled: boolean }>;
  capabilities: Directive<{
    enabled: boolean;
    allowedCapabilities: string[];
    requiredDropCapabilities: string[];
  }>;
  selinux: Directive<{
    enabled: boolean;
    allowedCapabilities: SeLinuxOption[];
  }>;
  allowProcMount: Directive<{
    enabled: boolean;
    procMountType: string;
  }>;
  appArmor: Directive<{
    enabled: boolean;
    AppArmorType: string[];
  }>;
  secComp: Directive<{
    enabled: boolean;
    secCompType: string[];
  }>;
  forbiddenSysctlsList: Directive<{
    enabled: boolean;
    requiredDropCapabilities: string[];
  }>;
}

/**
 * Default initial values for Kubernetes Security Policy (create mode)
 */
export const KUBERNETES_SECURITY_INITIAL_VALUES: KubernetesSecurityFormData = {
  restrictDefaultNamespace: false,
  restrictSecrets: false,
  enablePodSecurityConstraints: false,
  restrictPrivilegedContainers: false,
  restrictHostNamespace: false,
  restrictHostNetworkingPorts: false,
  hostNetwork: false,
  hostPortMin: 0,
  hostPortMax: 0,
  restrictVolumeTypes: false,
  allowedVolumeTypes: [],
  restrictHostFilesystemPaths: false,
  allowedHostPaths: [],
  restrictDrivers: false,
  allowedFlexVolumeDrivers: [],
  requireReadOnlyRootFilesystem: false,
  restrictUserAndGroupIds: false,
  runAsUserType: 'RunAsAny',
  runAsUserRanges: [],
  runAsGroupType: 'RunAsAny',
  runAsGroupRanges: [],
  supplementalGroupsType: 'RunAsAny',
  supplementalGroupsRanges: [],
  fsGroupType: 'RunAsAny',
  fsGroupRanges: [],
  restrictEscalationToRootPrivileges: false,
  restrictLinuxCapabilities: false,
  allowedCapabilities: [],
  requiredDropCapabilities: [],
  defaultAddCapabilities: [],
  restrictSeLinuxSecurityContext: false,
  seLinuxOptions: [],
  restrictProcMountTypes: false,
  restrictAppArmorProfiles: false,
  allowedAppArmorProfiles: [],
  restrictSeccompProfiles: false,
  allowedSeccompProfiles: [],
  restrictSysctlProfiles: false,
  forbiddenSysctls: [],
};
